import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowUpRight, MessageCircle } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { MapPanel } from "@/components/home/LocationBlock";
import { Input, Select, TextArea, Consent, Honeypot } from "@/components/site/Form";
import { SuccessNote, useLeadForm } from "@/components/vehicle/Forms";
import { api, vehicleTitle } from "@/lib/api";
import { useSite, fullAddress, What3Words } from "@/lib/site";
import { useSeo } from "@/hooks/useSeo";

const REASONS = ["Vehicle enquiry", "Sell my vehicle", "Part exchange", "Finance", "After-sales", "General enquiry"];

export default function Contact() {
  const site = useSite();
  const [params] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const { f, setF, errors, bind, busy, done, submit } = useLeadForm({ reason: params.get("reason") || "", vehicle: "" });
  useSeo({ title: "Contact Cirencester 4x4 | Directions & Appointments", description: "Contact Cirencester 4x4 in Cirencester, Gloucestershire. Viewings by appointment only. Directions and what3words location.", path: "/contact" });
  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(Array.isArray(r?.data) ? r.data : [])).catch(() => {}); }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    const v = vehicles.find((x) => x.id === f.vehicle);
    submit({ enquiry_type: f.reason || "General enquiry", lead_source: "Website - contact page", vehicle_id: v?.id || "", vehicle_title: v ? `${vehicleTitle(v)} ${v.derivative}` : "" },
      f.reason ? {} : { reason: "Please choose a reason" });
  };

  return (
    <main data-testid="contact-page">
      <PageHero eyebrow="Contact" title="Talk to us." sub="By appointment only." />
      <section className="mx-auto grid max-w-[1440px] gap-16 px-5 py-20 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-28">
        <div className="lg:col-span-5">
          <ul className="space-y-6 text-base">
            <li className="flex gap-4"><MapPin className="mt-1 h-5 w-5 shrink-0 text-forest" strokeWidth={1.5} /><span data-testid="contact-address">{fullAddress(site)}<What3Words site={site} className="mt-1 text-forest" testid="contact-w3w" /></span></li>
            <li className="flex gap-4"><Phone className="mt-1 h-5 w-5 shrink-0 text-forest" strokeWidth={1.5} /><a className="link-u" href={`tel:${site.phone_href}`} data-testid="contact-phone">{site.phone}</a></li>
            {site.whatsapp && <li className="flex gap-4"><MessageCircle className="mt-1 h-5 w-5 shrink-0 text-forest" strokeWidth={1.5} /><a className="link-u" href={`https://wa.me/${site.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp">Message us on WhatsApp</a></li>}
            <li className="flex gap-4"><Mail className="mt-1 h-5 w-5 shrink-0 text-forest" strokeWidth={1.5} /><a className="link-u" href={`mailto:${site.email}`} data-testid="contact-email">{site.email}</a></li>
            <li className="flex gap-4"><Clock className="mt-1 h-5 w-5 shrink-0 text-forest" strokeWidth={1.5} />
              <div className="w-full max-w-xs space-y-1">{(site.opening_hours || []).map((h) => <p key={h.day} className="flex justify-between gap-4"><span>{h.day}</span><span className="text-charcoal/75">{h.hours}</span></p>)}</div>
            </li>
          </ul>
          <a href={site.directions_url} target="_blank" rel="noreferrer" className="btn btn-secondary mt-10" data-testid="contact-directions">Get Directions <ArrowUpRight className="arrow h-4 w-4" /></a>
          <div className="mt-10 h-[340px]"><MapPanel /></div>
        </div>
        <div className="lg:col-span-6 lg:col-start-7">
          <h2 className="mb-8 font-serif text-4xl text-charcoal">Send us a message</h2>
          {done ? <SuccessNote title="Thank you — message received." testid="contact-success">We aim to reply within one working day.</SuccessNote> : (
            <form noValidate onSubmit={onSubmit} className="relative space-y-5" data-testid="contact-form">
              <Honeypot value={f.website} onChange={(v) => setF({ ...f, website: v })} />
              <div className="grid gap-5 sm:grid-cols-2">
                <Input id="c-name" label="Name" required autoComplete="name" {...bind("name")} />
                <Input id="c-email" label="Email" type="email" required autoComplete="email" {...bind("email")} />
                <Input id="c-phone" label="Telephone" type="tel" required autoComplete="tel" {...bind("phone")} />
                <Select id="c-reason" label="Reason for enquiry" required options={REASONS} {...bind("reason")} />
              </div>
              <Select id="c-vehicle" label="Vehicle of interest" placeholder="None / not sure" options={vehicles.map((v) => ({ value: v.id, label: `${vehicleTitle(v)} ${v.derivative}` }))} {...bind("vehicle")} />
              <TextArea id="c-message" label="Message" {...bind("message")} />
              <Consent id="c-consent" checked={f.consent} onChange={(v) => setF({ ...f, consent: v })} error={errors.consent} />
              <button type="submit" disabled={busy} className="btn btn-primary" data-testid="contact-submit">{busy ? "Sending…" : "Send message"}</button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
